'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Layers, ShoppingCart, Check, Tag } from 'lucide-react';
import type { Bundle } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useOwnedItems } from '@/context/OwnedItemsContext';
import { CoverPlaceholder } from '@/components/ui/CoverPlaceholder';
import { Ribbon } from '@/components/ui/Ribbon';

interface BundleCardProps {
  bundle: Bundle;
  /** Marks this card's cover image as the LCP candidate. Next's `priority` alone does not emit
   *  a `fetchpriority` attribute on this Next version, so both are passed explicitly. Opt-in and
   *  false by default so /harga, the homepage, etc. are unaffected. */
  priority?: boolean;
}

export function BundleCard({ bundle, priority = false }: BundleCardProps) {
  const t = useTranslations('bundleCardCatalog');
  const { addItem, openDrawer, isInCart } = useCart();
  const { ownedBundleIds } = useOwnedItems();

  const accentColor = bundle.examTypes[0]?.color ?? '#4f46e5';
  // bundle.price is the already-computed effective price (server-side
  // promo-window gated) — bundle.promoPrice is RAW and unconditional (not gated
  // on promoStartAt/promoEndAt). hasDiscount must compare against bundle.price,
  // and the price footer below must render bundle.price, not promoPrice
  // directly: outside the promo window the server collapses price back down to
  // basePrice, so `price < basePrice` is the only reliable "is discounted"
  // signal, and bundle.price is the only number that matches what checkout
  // actually charges (see CartDrawer's CartItemCard for the same pattern).
  const hasDiscount = bundle.promoPrice != null && bundle.price < bundle.basePrice;
  const discountPct = hasDiscount
    ? Math.round(((bundle.basePrice - bundle.price) / bundle.basePrice) * 100)
    : 0;

  const isOwned = ownedBundleIds.has(bundle.id);
  const inCart = isInCart(bundle.id);
  // One ribbon at most: a discount wins over the bundle label. An owned card shows
  // no price, so a lone discount would mean nothing there.
  const showDiscountRibbon = hasDiscount && !isOwned;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOwned || inCart) return;
    addItem({
      type: 'bundle',
      moduleId: bundle.id,
      title: bundle.title,
      price: bundle.price,
      basePrice: bundle.basePrice,
      promoPrice: bundle.promoPrice,
      isFree: bundle.price === 0,
      coverImageUrl: bundle.coverImageUrl,
    });
    openDrawer();
  };

  return (
    // The wrapper is not clipped, so the ribbon can overhang the card edge; the
    // Link keeps overflow-hidden for the rounded cover.
    <div className="relative h-full w-full">
    <Link
      href={`/tests/bundle/${bundle.slug}`}
      className="group flex flex-col h-full bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all overflow-hidden cursor-pointer"
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-100">
        {bundle.coverImageUrl ? (
          <Image
            src={bundle.coverImageUrl}
            alt={bundle.title}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover group-hover:scale-105 transition-transform duration-300"
            priority={priority}
            fetchPriority={priority ? 'high' : undefined}
          />
        ) : (
          <CoverPlaceholder label={bundle.examTypes[0]?.name ?? bundle.title} accentColor={bundle.examTypes[0]?.color ?? accentColor} />
        )}
      </div>

      <div className="p-4 sm:p-5 flex flex-col flex-1">
        {/* The bundle ribbon already says this; keep the pill when the ribbon shows a discount. */}
        {showDiscountRibbon && (
          <span className="inline-flex items-center gap-1 self-start text-[10px] font-bold px-2 py-1 rounded-full bg-teal-600 text-white mb-2.5 w-fit">
            <Layers className="w-3 h-3" />
            {t('paketLengkap')}
          </span>
        )}

        {/* Badges row */}
        <div className="flex items-center gap-1 flex-wrap mb-2">
          {bundle.examTypes.slice(0, 2).map((et) => (
            <span
              key={et.slug}
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${et.color ?? accentColor}15`, color: et.color ?? accentColor }}
            >
              {et.name}
            </span>
          ))}
        </div>

        <h3 className="font-bold text-slate-900 text-sm leading-snug mb-2.5 line-clamp-2 group-hover:text-teal-700 transition-colors">
          {bundle.title}
        </h3>

        {/* Meta */}
        <div className="flex items-center gap-3 text-xs text-slate-400 mb-4">
          <span className="flex items-center gap-1">
            <Layers className="w-3.5 h-3.5" />
            {t('modul', { count: bundle.moduleCount })}
          </span>
        </div>

        {/* Price + Cart button */}
        <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-x-2 gap-y-2 mt-auto">
          {isOwned ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-teal-600">
              <Check className="w-3.5 h-3.5" />
              {t('dimiliki')}
            </span>
          ) : hasDiscount ? (
            <span className="flex items-baseline gap-1.5 whitespace-nowrap">
              <span className="font-bold text-base text-orange-600">{`Rp ${bundle.price.toLocaleString('id-ID')}`}</span>
              <span className="text-xs text-slate-400 line-through">{`Rp ${bundle.basePrice.toLocaleString('id-ID')}`}</span>
            </span>
          ) : (
            <span className="font-bold text-base text-slate-900">{`Rp ${bundle.price.toLocaleString('id-ID')}`}</span>
          )}
          {isOwned ? null : inCart ? (
            <span className="ml-auto flex items-center gap-1 text-xs text-teal-600 font-semibold min-h-[36px]">
              <Check className="w-3.5 h-3.5" />
              {t('diKeranjang')}
            </span>
          ) : (
            <button
              onClick={handleAddToCart}
              className="ml-auto flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 min-h-[36px] rounded-lg transition-colors text-white bg-orange-500 hover:bg-orange-600"
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              {t('beli')}
            </button>
          )}
        </div>
      </div>
    </Link>
    {showDiscountRibbon ? (
      <Ribbon tone="discount" icon={Tag} label={`-${discountPct}%`} />
    ) : (
      <Ribbon tone="bundle" icon={Layers} label={t('paketLengkap')} />
    )}
    </div>
  );
}
